import {Box, IconButton, Separator, Stack, Text} from "@chakra-ui/react";
import {ArrowDownTrayIcon, ArrowsRightLeftIcon, CircleStackIcon, HandRaisedIcon} from "@heroicons/react/16/solid";
import {ComponentProps, Fragment, memo, ReactNode} from "react";
import {Tooltip} from "../../../../../../../../components/ui/tooltip";
import {BsCursorFill} from "react-icons/bs";
import {useUiPointerEvents} from "../../utility/uiPointerEventsContext";

const TOOLS = ["pan", "select", "createNode", "createConnection"] as const;
export type Tool = (typeof TOOLS)[number];
type ToolInfo = {index: number; label: string; icon: ReactNode};

const TOOL_INFOS = {
  pan: {index: 1, label: "Hand (panning tool)", icon: <HandRaisedIcon />},
  select: {index: 2, label: "Cursor (select tool)", icon: <BsCursorFill />},
  createNode: {index: 3, label: "Create node", icon: <CircleStackIcon />},
  createConnection: {index: 4, label: "Create connection", icon: <ArrowsRightLeftIcon />},
} as const satisfies Record<Tool, ToolInfo>;

const TOOLTIP_POSITIONING: ComponentProps<typeof Tooltip>["positioning"] = {placement: "right"};

const ToolButton = memo(
  ({tool, selectedTool, onToolChange}: {tool: Tool; selectedTool: Tool; onToolChange: (tool: Tool) => void}) => {
    return (
      <Tooltip content={TOOL_INFOS[tool].label} positioning={TOOLTIP_POSITIONING}>
        <Box position="relative">
          <IconButton
            variant={tool === selectedTool ? "subtle" : "ghost"}
            colorPalette="blue"
            size="sm"
            onClick={() => onToolChange(tool)}
            color={tool === selectedTool ? "fg" : "fg.muted"}
          >
            {TOOL_INFOS[tool].icon}
          </IconButton>
          <Text position="absolute" bottom="2px" right="3px" fontSize="2xs" color="fg.muted" pointerEvents="none">
            {TOOL_INFOS[tool].index}
          </Text>
        </Box>
      </Tooltip>
    );
  },
);

export const Toolbar = memo(
  ({
    selectedTool,
    onSelectedToolChange,
    onClickDownload,
  }: {
    selectedTool: Tool;
    onSelectedToolChange: (tool: Tool) => void;
    onClickDownload: () => void;
  }) => {
    const pointerEvents = useUiPointerEvents();

    return (
      <Stack pointerEvents={pointerEvents}>
        <Stack shadow="md" p={1} bg="white" overflow="hidden" rounded="md" w="min-content">
          {TOOLS.map((tool, index) => (
            <Fragment key={tool}>
              <ToolButton tool={tool} selectedTool={selectedTool} onToolChange={onSelectedToolChange} />
              {index === 1 && <Separator />}
            </Fragment>
          ))}
        </Stack>
        <Stack shadow="md" p={1} bg="white" overflow="hidden" rounded="md" w="min-content">
          <Tooltip content="Export...">
            <IconButton variant="ghost" colorPalette="blue" size="sm" color="fg.muted" onClick={onClickDownload}>
              <ArrowDownTrayIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    );
  },
);
